/**
 * Bob Laadt Op — bob-product-hero (productpagina).
 *
 * 1. Galerij: duimnagels + pijltjes wisselen de hoofdfoto (ontwerp r. 2346–2364).
 *    Alle foto's staan al in het fotovlak; we wisselen alleen .is-active en de
 *    weergave-class (is-scene / is-flat) op het vlak.
 * 2. Accessoires: aantikken (aria-pressed) → live totaal; vervaging onderaan het
 *    scrollvak verdwijnt als je onderaan bent (ontwerp r. 2489–2531).
 * 3. Capaciteit kiezen: basis + 0…n uitbreidingsmodules (bob.uitbreiding); prijs,
 *    prijsnoot en accessoiretotaal rekenen live mee.
 * 4. "Nu bestellen": hoofdvariant + modules + gekozen accessoires in één POST naar
 *    /cart/add.js, en Horizon's CartLinesUpdateEvent zodat de winkelmand-teller
 *    en de winkelmand-la bijwerken (zoals assets/product-form.js dat doet).
 *
 * Alle teksten komen via data-attributen uit Liquid (vertaalbaar).
 * Custom element: werkt opnieuw na een re-render in de theme editor.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Bedrag (centen) in de geldnotatie van de winkel. Ronde bedragen zonder
 * decimalen, zoals in het ontwerp ("€ 789") en snippets/bob-price (plain).
 * @param {number} cents
 * @param {string} format - shop.money_format, bijv. "€{{amount_with_comma_separator}}"
 */
function formatMoney(cents, format) {
  const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
  const kind = match ? match[1] : 'amount';
  const round = cents % 100 === 0;
  /** @param {string} thousands @param {string} decimal @param {boolean} [noDecimals] */
  const fmt = (thousands, decimal, noDecimals = false) => {
    const [int, dec] = (cents / 100).toFixed(noDecimals || round ? 0 : 2).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (dec ? decimal + dec : '');
  };
  /** @type {Record<string, string>} */
  const values = {
    amount: fmt(',', '.'),
    amount_no_decimals: fmt(',', '.', true),
    amount_with_comma_separator: fmt('.', ','),
    amount_no_decimals_with_comma_separator: fmt('.', ',', true),
    amount_with_space_separator: fmt(' ', ','),
    amount_no_decimals_with_space_separator: fmt(' ', ',', true),
    amount_with_apostrophe_separator: fmt("'", '.'),
  };
  const value = values[kind] ?? values.amount;
  return match ? format.replace(match[0], value) : value;
}

class BobProductHero extends HTMLElement {
  /** @type {(() => void) | undefined} Werkt het accessoiretotaal bij (gezet door #initExtras). */
  refreshExtras;

  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.#initGallery(signal);
    this.#initExtras(signal);
    this.#initCapacity(signal);

    const form = /** @type {HTMLFormElement|null} */ (this.querySelector('form[data-type="add-to-cart-form"]'));
    form?.addEventListener('submit', (e) => this.#onSubmit(e, form), { signal });

    /* Theme editor: een accessoire-block kiezen scrolt hem in beeld in de lijst. */
    document.addEventListener(
      'shopify:block:select',
      (e) => {
        const target = /** @type {HTMLElement} */ (e.target);
        if (this.contains(target)) target.scrollIntoView({ block: 'nearest' });
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  /* ── 1. Galerij ──────────────────────────────────────────────────── */
  /** @param {AbortSignal} signal */
  #initGallery(signal) {
    const frame = this.querySelector('[data-photo-main]');
    if (!frame) return;
    const photos = Array.from(frame.querySelectorAll('img[data-mode]'));
    const thumbs = Array.from(this.querySelectorAll('.pdp-thumb[data-thumb]'));
    if (photos.length < 2) return;
    let active = 0;

    /** @param {number} index */
    const show = (index) => {
      active = (index + photos.length) % photos.length;
      photos.forEach((img, i) => img.classList.toggle('is-active', i === active));
      thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === active);
        if (i === active) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      const mode = /** @type {HTMLElement} */ (photos[active]).dataset.mode;
      frame.classList.toggle('is-scene', mode === 'scene');
      frame.classList.toggle('is-flat', mode === 'flat');
    };

    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => show(i), { signal }));
    this.querySelector('[data-photo-prev]')?.addEventListener('click', () => show(active - 1), { signal });
    this.querySelector('[data-photo-next]')?.addEventListener('click', () => show(active + 1), { signal });
  }

  /* ── 2. Accessoires: selectie, totaal en vervaging ─────────────────── */
  /** @param {AbortSignal} signal */
  #initExtras(signal) {
    const total = /** @type {HTMLElement|null} */ (this.querySelector('[data-extras-total]'));
    if (!total) return;
    const buttons = /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[data-extra]')));
    const base = Number(total.dataset.base) || 0;
    const label = total.querySelector('[data-extras-label]');
    const amount = total.querySelector('[data-extras-amount]');
    const moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}}';

    const update = () => {
      const chosen = this.#chosenExtras();
      const sum = chosen.reduce((n, b) => n + (Number(b.dataset.price) || 0), base + this.#capacity().price);
      if (label) {
        /* Met capaciteitskiezer: totale opslag erachter, " · 7,56 kWh" (ontwerp 09-10-2026). */
        const kwh = this.#capacity().kwh;
        const suffix = total.dataset.labelKwh && kwh ? total.dataset.labelKwh.replace('[kwh]', kwh) : '';
        label.textContent =
          (chosen.length === 0
            ? total.dataset.labelEmpty || ''
            : chosen.length === 1
              ? total.dataset.labelOne || ''
              : (total.dataset.labelOther || '').replace('[n]', String(chosen.length))) + suffix;
      }
      if (amount) amount.textContent = formatMoney(sum, moneyFormat);
    };
    this.refreshExtras = update;

    buttons.forEach((b) =>
      b.addEventListener(
        'click',
        () => {
          b.setAttribute('aria-pressed', b.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
          update();
        },
        { signal }
      )
    );

    /* De vervaging onderaan het scrollvak verdwijnt zodra je onderaan bent;
       hij zou daar alleen nog het laatste item afdekken. */
    const box = this.querySelector('[data-extras-scroll]');
    const list = box?.querySelector('.extras-lijst');
    if (!box || !list) return;
    const fade = () => {
      const bottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 2;
      box.classList.toggle('is-onderaan', bottom);
    };
    list.addEventListener('scroll', fade, { passive: true, signal });
    window.addEventListener('resize', fade, { signal });
    fade();
  }

  /** @returns {HTMLButtonElement[]} */
  #chosenExtras() {
    return /** @type {HTMLButtonElement[]} */ (
      Array.from(this.querySelectorAll('[data-extra][aria-pressed="true"]:not(:disabled)'))
    );
  }

  /* ── 3. Capaciteit kiezen ─────────────────────────────────────────── */
  /** @returns {{ units: number, price: number, kwh: string }} */
  #capacity() {
    const checked = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-capacity] input:checked'));
    return {
      units: Number(checked?.value) || 0,
      price: Number(checked?.dataset.price) || 0,
      kwh: checked?.dataset.kwh || '',
    };
  }

  /** @param {AbortSignal} signal */
  #initCapacity(signal) {
    const box = this.querySelector('[data-capacity]');
    if (!box) return;
    const priceEl = /** @type {HTMLElement|null} */ (this.querySelector('[data-price-value]'));
    const note = /** @type {HTMLElement|null} */ (this.querySelector('[data-price-note]'));
    const base = Number(priceEl?.dataset.price) || 0;
    const baseNote = note?.textContent ?? '';
    const moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}}';

    const update = () => {
      const { units, price, kwh } = this.#capacity();
      const sum = base + price;
      /* Zelfde notatie als snippets/bob-price: ronde bedragen als "€ 789,-". */
      if (priceEl) priceEl.textContent = formatMoney(sum, moneyFormat) + (sum % 100 === 0 ? ',-' : '');
      if (note) {
        const template = note.dataset.noteCapacity || '';
        note.textContent = units > 0 && template ? template.replace('[kwh]', kwh) : baseNote;
      }
      this.refreshExtras?.();
    };

    box.addEventListener('change', update, { signal });
    /* Na terug-navigeren zet de browser soms een eerdere keuze terug. */
    update();
  }

  /* ── 4. Nu bestellen → winkelmand ──────────────────────────────────── */
  /**
   * @param {SubmitEvent} event
   * @param {HTMLFormElement} form
   */
  async #onSubmit(event, form) {
    event.preventDefault();
    const button = /** @type {HTMLButtonElement|null} */ (form.querySelector('[data-add-button]'));
    const msg = form.querySelector('[data-form-msg]');
    const variantInput = /** @type {HTMLInputElement|null} */ (form.querySelector('[data-variant-id]'));
    if (!variantInput || button?.disabled) return;

    const capacity = /** @type {HTMLElement|null} */ (this.querySelector('[data-capacity]'));
    const { units } = this.#capacity();
    const items = [
      { id: Number(variantInput.value), quantity: 1 },
      ...(capacity && units > 0 ? [{ id: Number(capacity.dataset.variantId), quantity: units }] : []),
      ...this.#chosenExtras().map((b) => ({ id: Number(b.dataset.variantId), quantity: 1 })),
    ];
    const itemCount = items.length;

    /* Secties van Horizon's winkelmand (la/pagina) meteen opnieuw laten renderen. */
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);

    if (msg) {
      msg.textContent = '';
      msg.classList.remove('visually-hidden');
    }
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    const refreshCart = async () => {
      const res = await fetch(this.dataset.cartUrl || '/cart.js', { headers: { Accept: 'application/json' } });
      return res.json();
    };

    try {
      const res = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, ...(sectionIds.length ? { sections: sectionIds.join(',') } : {}) }),
      });
      const data = await res.json();

      if (!res.ok || data.status) {
        /* Shopify geeft zelf een (vertaalde) omschrijving, bijv. bij te weinig voorraad. */
        const error = data.description || data.message || this.dataset.error || '';
        if (msg) msg.textContent = error;
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || error,
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        const cart = await refreshCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'bob-product-hero', itemCount, productId: this.dataset.productId },
        });
        return;
      }

      const cart = await refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'bob-product-hero',
          itemCount,
          productId: this.dataset.productId,
          sections: data.sections,
        },
      });

      /* Alleen voor schermlezers: "Toegevoegd aan je winkelmand". */
      if (msg) {
        msg.classList.add('visually-hidden');
        msg.textContent = this.dataset.added || '';
        setTimeout(() => {
          if (msg.classList.contains('visually-hidden')) msg.textContent = '';
        }, 4000);
      }
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      if (msg) msg.textContent = this.dataset.error || '';
      this.dispatchEvent(
        new CartErrorEvent({
          error: /** @type {Error} */ (error)?.message || 'Network error',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }
}

if (!customElements.get('bob-product-hero')) customElements.define('bob-product-hero', BobProductHero);
